import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, HomeIcon, FolderIcon, UsersIcon, BarChart3Icon, SettingsIcon, LogOutIcon, PlusIcon, HelpCircleIcon, LinkIcon, BuildingIcon, LayoutDashboardIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}
const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose
}) => {
  const [activeItem, setActiveItem] = useState('home');
  const [showJoinCode, setShowJoinCode] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [isOrgExpanded, setIsOrgExpanded] = useState(false);
  const userRole = 'admin'; // This would come from auth context in production
  const navItems = [{
    id: 'home',
    label: 'Home',
    icon: HomeIcon
  }];
  const orgSubItems = [{
    id: 'org-overview',
    label: 'Overview',
    icon: LayoutDashboardIcon,
    roles: ['admin', 'educator']
  }, {
    id: 'org-members',
    label: 'Members',
    icon: UsersIcon,
    roles: ['admin']
  }];
  const secondaryNavItems = [{
    id: 'workspace',
    label: 'Workspace',
    icon: FolderIcon
  }, {
    id: 'classes',
    label: 'Classes',
    icon: UsersIcon
  }, {
    id: 'reports',
    label: 'Reports',
    icon: BarChart3Icon
  }];
  const visibleOrgSubItems = orgSubItems.filter(item => item.roles.includes(userRole));
  const showOrganization = userRole !== 'independent';
  const bottomItems = [{
    id: 'help',
    label: 'Help & Support',
    icon: HelpCircleIcon
  }, {
    id: 'settings',
    label: 'Settings',
    icon: SettingsIcon
  }, {
    id: 'logout',
    label: 'Logout',
    icon: LogOutIcon
  }];
  const handleJoinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Joining with code:', joinCode);
    setShowJoinCode(false);
    setJoinCode('');
    onClose();
  };
  return <AnimatePresence data-id="element-1773">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden" onClick={onClose} data-id="element-1774" />
          {/* Menu Drawer */}
          <motion.div initial={{
        x: '-100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '-100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed left-0 top-0 bottom-0 w-80 bg-surface-elevated shadow-2xl z-50 md:hidden flex flex-col" data-id="element-1775">
            {/* Header */}
            <div className="p-4 border-b border-custom flex items-center justify-between bg-[#00B75F]" data-id="element-1776">
              <div className="flex items-center gap-3" data-id="element-1777">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qarEDkTctoyPPpJPJss7Dk/AA_-_horizontal_v_-_light_green_bg2x.png" alt="Ainstein Logo" className="h-8 w-auto" data-id="element-1778" />
              </div>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Close menu" data-id="element-1779">
                <XIcon size={20} className="text-white" data-id="element-1780" />
              </button>
            </div>
            {/* Create Assessment Button */}
            <div className="p-4" data-id="element-1781">
              <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#00B75F] to-[#00a055] text-white font-semibold rounded-xl shadow-green-glow hover:shadow-lg transition-all duration-200 min-h-[44px]" onClick={onClose} data-id="element-1782">
                <PlusIcon size={20} data-id="element-1783" />
                <span className="text-sm" data-id="element-1784">Create Assessment</span>
              </button>
            </div>
            {/* Join Code Section */}
            <div className="px-4 pb-4" data-id="element-1785">
              {!showJoinCode ? <button onClick={() => setShowJoinCode(true)} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-surface border-2 border-custom text-text-primary rounded-xl transition-all duration-200 min-h-[44px] hover:bg-surface-elevated" data-id="element-1786">
                  <LinkIcon size={18} data-id="element-1787" />
                  <span className="text-sm font-medium" data-id="element-1788">Join with Code</span>
                </button> : <form onSubmit={handleJoinSubmit} className="space-y-2" data-id="element-1789">
                  <input type="text" placeholder="Enter join code" value={joinCode} onChange={e => setJoinCode(e.target.value)} className="w-full px-4 py-3 text-sm bg-surface border-2 border-custom rounded-xl text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent min-h-[44px]" autoFocus data-id="element-1790" />
                  <div className="flex gap-2" data-id="element-1791">
                    <button type="submit" className="flex-1 px-4 py-2.5 bg-[#00B75F] text-white rounded-lg text-sm font-semibold hover:bg-[#00a055] transition-colors min-h-[44px]" data-id="element-1792">
                      Join
                    </button>
                    <button type="button" onClick={() => {
                setShowJoinCode(false);
                setJoinCode('');
              }} className="px-4 py-2.5 bg-surface border-2 border-custom text-text-secondary rounded-lg text-sm font-semibold hover:bg-surface-elevated transition-colors min-h-[44px]" data-id="element-1793">
                      Cancel
                    </button>
                  </div>
                </form>}
            </div>
            {/* Navigation Items */}
            <nav className="flex-1 p-4 space-y-2 overflow-y-auto" data-id="element-1794">
              {/* Organization Section */}
              {showOrganization && <div className="space-y-2" data-id="element-1795">
                  <button onClick={() => setIsOrgExpanded(!isOrgExpanded)} className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all duration-200 min-h-[44px] ${activeItem.startsWith('org-') ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]' : 'text-text-secondary hover:bg-surface hover:text-text-primary'}`} data-id="element-1796">
                    <div className="flex items-center gap-3" data-id="element-1797">
                      <BuildingIcon size={20} data-id="element-1798" />
                      <span className="font-medium text-sm" data-id="element-1799">Organization</span>
                    </div>
                    {isOrgExpanded ? <ChevronDownIcon size={16} data-id="element-1800" /> : <ChevronRightIcon size={16} data-id="element-1801" />}
                  </button>

                  <AnimatePresence data-id="element-1802">
                    {isOrgExpanded && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} className="pl-4 space-y-2 overflow-hidden" data-id="element-1803">
                        {visibleOrgSubItems.map(item => {
                  const Icon = item.icon;
                  const isActive = activeItem === item.id;
                  return <button key={item.id} onClick={() => {
                    setActiveItem(item.id);
                    onClose();
                  }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 min-h-[44px] ${isActive ? 'bg-[#00B75F] text-white shadow-green-glow' : 'text-text-secondary hover:bg-surface hover:text-text-primary'}`} data-id="element-1804">
                              <Icon size={18} data-id="element-1805" />
                              <span className="font-medium text-sm" data-id="element-1806">
                                {item.label}
                              </span>
                            </button>;
                })}
                      </motion.div>}
                  </AnimatePresence>
                </div>}

              {/* Home */}
              {navItems.map(item => {
            const Icon = item.icon;
            const isActive = activeItem === item.id;
            return <button key={item.id} onClick={() => {
              setActiveItem(item.id);
              onClose();
            }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 min-h-[44px] ${isActive ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F] shadow-green-glow' : 'text-text-secondary hover:bg-surface hover:text-text-primary'}`} data-id="element-1807">
                    <Icon size={20} data-id="element-1808" />
                    <span className="font-medium text-sm" data-id="element-1809">{item.label}</span>
                  </button>;
          })}

              <div className="h-px bg-custom my-2" data-id="element-1810" />

              {/* Secondary Navigation */}
              {secondaryNavItems.map(item => {
            const Icon = item.icon;
            const isActive = activeItem === item.id;
            return <button key={item.id} onClick={() => {
              setActiveItem(item.id);
              onClose();
            }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 min-h-[44px] ${isActive ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F] shadow-green-glow' : 'text-text-secondary hover:bg-surface hover:text-text-primary'}`} data-id="element-1811">
                    <Icon size={20} data-id="element-1812" />
                    <span className="font-medium text-sm" data-id="element-1813">{item.label}</span>
                  </button>;
          })}
            </nav>
            {/* Bottom Items */}
            <div className="p-4 space-y-2 border-t border-custom bg-surface" data-id="element-1814">
              {bottomItems.map(item => {
            const Icon = item.icon;
            return <button key={item.id} onClick={onClose} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-text-secondary hover:bg-surface-elevated hover:text-text-primary transition-all duration-200 min-h-[44px]" data-id="element-1815">
                    <Icon size={20} data-id="element-1816" />
                    <span className="font-medium text-sm" data-id="element-1817">{item.label}</span>
                  </button>;
          })}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};
export default MobileMenu;